<template>
  <div class="map-view-toggle" :class="`map-view-toggle--${variant}`">
    <button
      class="map-view-toggle-btn"
      :class="{ active: mapViewMode === 'aoi' }"
      :title="$t('viewToggle.aoiTitle')"
      @click="mapViewMode = 'aoi'"
    >
      <span>{{ $t('viewToggle.aoi') }}</span>
    </button>
    <button
      class="map-view-toggle-btn"
      :class="{ active: mapViewMode === 'grid' }"
      :title="$t('viewToggle.gridTitle')"
      @click="mapViewMode = 'grid'"
    >
      <span>{{ $t('viewToggle.grid') }}</span>
    </button>
    <button
      class="map-view-toggle-btn"
      :class="{ active: mapViewMode === 'swath' }"
      :title="$t('viewToggle.swathTitle')"
      @click="mapViewMode = 'swath'"
    >
      <span>{{ $t('viewToggle.swaths') }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { useFilterStore } from '@/composables/useFilterStore';

withDefaults(
  defineProps<{
    variant?: 'overlay' | 'topbar';
  }>(),
  {
    variant: 'overlay',
  },
);

const { mapViewMode } = useFilterStore();
</script>

<style lang="scss">
.map-view-toggle {
  z-index: 100;
  display: inline-flex;
  gap: 0;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--p-overlay-border-color, rgba(255, 255, 255, 0.12));

  &--overlay {
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }

  &--topbar {
    position: static;
    transform: none;
    box-shadow: none;
    background: rgba(250, 249, 247, 0.08);
    border-color: rgba(250, 249, 247, 0.12);
    backdrop-filter: blur(10px);
  }

  &-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 6px 12px;
    border: none;
    cursor: pointer;
    font-size: 0.78rem;
    font-weight: 500;
    font-family: inherit;
    white-space: nowrap;
    transition: background 0.15s, color 0.15s;
    background: var(--p-overlay-background, #1e293b);
    color: var(--p-text-muted-color, #94a3b8);

    &:hover {
      background: var(--p-content-hover-background, #334155);
      color: var(--p-text-color, #e2e8f0);
    }

    &.active {
      background: var(--p-primary-color, #00bfa5);
      color: #fff;
    }

    &:not(:last-child) {
      border-right: 1px solid var(--p-overlay-border-color, rgba(255, 255, 255, 0.08));
    }
  }
}

.map-view-toggle--topbar .map-view-toggle-btn {
  min-width: 4.5rem;
  padding: 0.38rem 0.82rem;
  background: transparent;
  color: rgba(250, 249, 247, 0.72);

  &:hover {
    background: rgba(250, 249, 247, 0.1);
    color: rgba(250, 249, 247, 0.96);
  }

  &.active {
    background: rgba(3, 222, 204, 0.18);
    color: #03decc;
  }

  &:not(:last-child) {
    border-right-color: rgba(250, 249, 247, 0.08);
  }
}

:root:not(.color-scheme-dark) .map-view-toggle {
  border-color: rgba(15, 23, 42, 0.14);

  &--overlay {
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.14);
  }

  &--topbar {
    background: rgba(15, 23, 42, 0.06);
  }

  .map-view-toggle-btn {
    background: #ffffff;
    color: #334155;

    &:hover {
      background: #f1f5f9;
      color: #0f172a;
    }

    &.active {
      background: var(--p-primary-color, #00bfa5);
      color: #ffffff;
    }

    &:not(:last-child) {
      border-right-color: rgba(15, 23, 42, 0.1);
    }
  }
}

:root:not(.color-scheme-dark) .map-view-toggle--topbar .map-view-toggle-btn {
  background: transparent;
  color: #334155;

  &:hover {
    background: rgba(15, 23, 42, 0.08);
    color: #0f172a;
  }

  &.active {
    background: rgba(0, 191, 165, 0.16);
    color: #0f766e;
  }
}

@media (max-width: 640px) {
  .map-view-toggle--topbar .map-view-toggle-btn {
    min-width: 0;
    padding-inline: 0.65rem;
    font-size: 0.74rem;
  }
}
</style>
